import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import {
  Check,
  X,
  ArrowRight,
  ShieldCheck,
  MapPin,
  Users,
  Lock,
  LifeBuoy,
  Star,
  Clock,
  UtensilsCrossed,
  Martini,
  Croissant,
  Coffee,
} from "lucide-react";
import { Link } from "wouter";
import { Expandable } from "../components/ux/Expandable";
import Logo from "@/components/Logo";
import { useT } from "@/lib/i18n";
import tracking from "@/lib/tracking";
import { quizHref, StickyCta, ProofStrip } from "./shared";

/**
 * Long-form conversion landing layout: the full DTC "everything on one page"
 * anatomy proven by the dinner-matching competitors (Mingleo, 2026-08):
 * hero, coverage, mechanism, self-qualifier, formats, founder story, benefits,
 * disqualifier, why-it-works, proof, safety, FAQ, emotional close.
 *
 * The whole page is one long objection-handling funnel: every section answers
 * the question the previous one raises, and a CTA sits after each answered
 * objection. Unlike the short single-idea templates (chat, receipt, document)
 * this one is meant for warm/mid-funnel traffic that wants to read before it
 * commits. MCP template name: "longform".
 *
 * Audience wording: women 50+ are addressed as "lesbian", younger women as
 * "sapphic". The booking param stays "s" for both; only the copy differs.
 *
 * Outclose sourcing rules baked in: quotes must be REAL members (see the
 * `testimonials` field), stats must be re-derived from post_dinner_feedback,
 * imagery must be approved photo-library assets, and copy must not anchor a
 * weekday or a single format (days rotate per city, formats vary by market).
 */

const APP_URL = "https://app.outclose.com/";

/** One curated Outing on the dates band. `cityIds: null` = runs everywhere. */
interface UpcomingOuting {
  date: string;
  time: string;
  type: "dinner" | "brunch" | "drinks" | "coffee";
  cityIds: string[] | null;
}

/** Real upcoming Outings from /api/landing/upcoming. Public, cached, no auth. */
function useUpcomingOutings(enabled: boolean) {
  return useQuery({
    queryKey: ["landing", "upcoming-events"],
    enabled,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<UpcomingOuting[]> => {
      const res = await fetch("/api/landing/upcoming/events?limit=6");
      if (!res.ok) throw new Error("Failed to load upcoming Outings");
      const body = await res.json();
      return body?.data ?? [];
    },
  });
}

interface LandingCity {
  id: string;
  name: string;
  emoji?: string;
  available?: boolean;
}

/** Cities we actively serve, for the band's picker. */
function useActiveCities(enabled: boolean) {
  return useQuery({
    queryKey: ["landing", "cities"],
    enabled,
    staleTime: 30 * 60 * 1000,
    queryFn: async (): Promise<LandingCity[]> => {
      const res = await fetch("/api/user/cities?activeOnly=true&limit=200");
      if (!res.ok) throw new Error("Failed to load cities");
      const body = await res.json();
      return body?.data ?? [];
    },
  });
}

/** The visitor's city, resolved server-side from edge geo headers. No prompt. */
function useDetectedCity(enabled: boolean) {
  return useQuery({
    queryKey: ["landing", "detected-city"],
    enabled,
    staleTime: Infinity,
    queryFn: async (): Promise<LandingCity | null> => {
      const res = await fetch("/api/landing/city");
      if (!res.ok) return null;
      const body = await res.json();
      return body?.data ?? null;
    },
  });
}

/**
 * Per-format identity for the dates band. Each Outing type gets its own icon
 * and accent so a scannable list reads as four different things to do, not one
 * repeated row. Icons are lucide (already a dependency), so they carry the same
 * stroke weight as the rest of the page and inherit currentColor in both themes.
 *
 * The label is a locale key, not a string: the band is chrome the page renders
 * for itself, so unlike `config` copy it has to follow the variant's language.
 */
const TYPE_META: Record<
  UpcomingOuting["type"],
  { labelKey: string; Icon: typeof UtensilsCrossed; chip: string; medallion: string }
> = {
  dinner: {
    labelKey: "longform.formats.dinner",
    Icon: UtensilsCrossed,
    chip: "bg-primary-500/10 text-primary-700 dark:text-primary-400",
    medallion: "bg-primary-500/10 text-primary-500",
  },
  drinks: {
    labelKey: "longform.formats.drinks",
    Icon: Martini,
    chip: "bg-secondary-500/10 text-secondary-700 dark:text-secondary-300",
    medallion: "bg-secondary-500/10 text-secondary-500 dark:text-secondary-300",
  },
  brunch: {
    labelKey: "longform.formats.brunch",
    Icon: Croissant,
    chip: "bg-amber-500/10 text-amber-800 dark:text-amber-400",
    medallion: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
  },
  coffee: {
    labelKey: "longform.formats.coffee",
    Icon: Coffee,
    chip: "bg-accent-500/10 text-accent-800 dark:text-accent-300",
    medallion: "bg-accent-500/10 text-accent-600 dark:text-accent-300",
  },
};

/**
 * Dates and times in the band are chrome, so they follow the page language.
 *
 * Hardcoding "en-US" printed "Thursday, August 27" and "7 PM" on a German
 * page whose every other word was German. Intl also settles 12h-vs-24h per
 * locale, which is not something a hand-rolled AM/PM branch can get right.
 */
function formatOutingDate(date: string, locale: string): string {
  // Parse as local, not UTC: "2026-08-27" must not drift a day backwards.
  const [y, m, d] = date.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(locale, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
}

function formatOutingTime(time: string, locale: string): string {
  const [h, min] = time.split(":").map(Number);
  const at = new Date(2000, 0, 1, h, min || 0);
  // On the hour, drop the ":00": "7 PM", "19 Uhr". Otherwise show minutes.
  return at.toLocaleTimeString(locale, {
    hour: "numeric",
    ...(min ? { minute: "2-digit" as const } : {}),
  });
}

export interface LongformStep {
  title: string;
  body: string;
}

export interface LongformFormat {
  name: string;
  body: string;
}

export interface LongformFeature {
  title: string;
  body: string;
}

export interface LongformTestimonial {
  quote: string;
  name: string;
  age: number;
  city: string;
}

export interface LongformStat {
  value: string;
  label: string;
}

export interface LongformConfig {
  template?: "longform";
  posthogEvent: string;
  audienceParam: string;
  eventTypeParam?: string;

  /** Hero */
  headline: string;
  subline: string;
  cta: string;
  ctaSub?: string;
  /** Optional approved image URLs. Omit and the hero renders type-only. */
  heroImages?: string[];

  /** Live "Outings near you" band. Cities and dates come from the API. */
  coverage?: {
    /**
     * Kicker above the picker. `{cities}` is replaced with the live count of
     * cities we serve, so the number can never go stale against the DB.
     */
    title: string;
    /** Section headline. This band is the page's proof, so it gets one. */
    headline: string;
    /** One line under the headline. */
    subline?: string;
    /** Shown when we know the city but have no open dates there yet. */
    emptyLabel: string;
    cta: string;
  };
  /** Group-size badge on each date card, e.g. "4 to 8 people". */
  groupSizeBadge?: string;

  /** How it works */
  stepsTitle: string;
  steps: LongformStep[];

  /** Self-qualifier */
  qualifier?: { question: string; yes: string; no: string; noReply: string };

  /** Formats */
  formatsTitle: string;
  formatsIntro: string;
  formats: LongformFormat[];

  /** Founder story */
  story?: { headline: string; body: string[] };

  /** Benefits */
  benefitsTitle: string;
  benefits: string[];

  /** Disqualifier */
  notForYouTitle: string;
  notForYou: string[];

  /** Why it works */
  whyTitle: string;
  why: LongformFeature[];

  /** Optional stats band. Only ever real numbers. Omit for proof strip alone. */
  stats?: LongformStat[];

  /**
   * Real member quotes only. Source them from `payment.testimonials.<segment>`
   * in the locale files, with attribution from `data/paywallTestimonials.ts`.
   * Never write a quote for this section.
   */
  testimonials?: { title: string; items: LongformTestimonial[] };

  /** Safety */
  safetyTitle: string;
  safety: LongformFeature[];

  /** FAQ */
  faqTitle: string;
  faq: Array<{ q: string; a: string }>;

  /** Emotional close */
  close: { headline: string; sub: string; body: string[] };

  /** Header/footer log-in label. Authored per language. */
  loginLabel?: string;
  ownedLabel?: "gay" | "lgbtq";
}

/**
 * Scroll-reveal props, collapsed to nothing under `prefers-reduced-motion`.
 * This page animates ~54 nodes; without the guard every one of them moves for
 * a visitor who asked the OS for stillness. Matches the house convention in
 * pages/static/Host.tsx.
 */
function useFadeUp() {
  const reduce = useReducedMotion();
  return reduce
    ? ({ initial: false } as const)
    : ({
        initial: { opacity: 0, y: 18 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, margin: "-60px" },
        transition: { duration: 0.45 },
      } as const);
}

function Cta({
  href,
  label,
  sub,
  size = "lg",
}: {
  href: string;
  label: string;
  sub?: string;
  size?: "lg" | "md";
}) {
  return (
    <div className="flex flex-col items-center">
      <a
        href={href}
        className={`rounded-full text-white font-bold inline-flex items-center justify-center gap-2 transition-colors shadow-lg ${
          size === "lg"
            ? "text-xl px-10 py-4 bg-primary-600 hover:bg-primary-700 shadow-primary-600/30"
            : "text-lg px-8 py-3.5 bg-primary-700 hover:bg-primary-800 shadow-primary-700/30"
        }`}
      >
        {label}
        <ArrowRight className="w-5 h-5" />
      </a>
      {sub && <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-3">{sub}</p>}
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="font-serif text-3xl leading-[1.15] md:text-[42px] md:leading-[1.15] pb-1 tracking-tight text-neutral-900 dark:text-neutral-50 text-center">
      {children}
    </h2>
  );
}

export default function LandingLongform({ config }: { config: LongformConfig }) {
  const { t: tRaw, language } = useT("landing");
  const fadeUp = useFadeUp();
  const reduceMotion = useReducedMotion();
  const [qualified, setQualified] = useState<null | boolean>(null);

  useEffect(() => {
    tracking.trackPage(config.posthogEvent);
  }, [config.posthogEvent]);

  const ctaHref = quizHref(config.audienceParam, config.eventTypeParam);
  // A variant may author its own log-in label; otherwise take the app's, which
  // is already translated in every locale.
  const loginLabel = config.loginLabel ?? tRaw("app:common.buttons.login");

  // "Outings near you" band. Only fetched when the band is configured.
  const bandOn = Boolean(config.coverage);
  const { data: cities = [], isLoading: citiesLoading } = useActiveCities(bandOn);
  const { data: detectedCity } = useDetectedCity(bandOn);
  const { data: outings = [], isLoading: outingsLoading } = useUpcomingOutings(bandOn);

  // The visitor's pick wins; otherwise the edge-detected city; otherwise the
  // first city we serve, so the band is never an empty picker.
  const [pickedCityId, setPickedCityId] = useState<string | null>(null);
  const selectedCityId = pickedCityId ?? detectedCity?.id ?? cities[0]?.id ?? null;
  const selectedCity = useMemo(
    () => cities.find((c) => c.id === selectedCityId) ?? detectedCity ?? null,
    [cities, selectedCityId, detectedCity],
  );

  // A date with no city scope runs everywhere; a scoped one only where listed.
  const cityOutings = useMemo(
    () =>
      outings
        .filter((o) => !o.cityIds || (selectedCityId ? o.cityIds.includes(selectedCityId) : true))
        .slice(0, 3),
    [outings, selectedCityId],
  );

  // The quiz collects the city itself; these ride along as PostHog context on
  // quiz-start so we can tell which city and date drove the click.
  const bookHref = quizHref(config.audienceParam, config.eventTypeParam, {
    city: selectedCityId,
  });

  return (
    <div className="min-h-full bg-white dark:bg-neutral-900 text-neutral-800 dark:text-neutral-200 pb-28 md:pb-0">
      {/* header */}
      <header className="sticky top-0 z-40 bg-white/85 dark:bg-neutral-900/85 backdrop-blur border-b border-neutral-200/70 dark:border-neutral-800">
        <div className="max-w-5xl mx-auto px-5 h-16 flex items-center justify-between">
          <Logo withIcon={true} />
          <div className="flex items-center gap-2">
            <a
              href={APP_URL}
              className="hidden sm:inline-flex text-sm font-semibold text-neutral-600 dark:text-neutral-300 hover:text-primary-500 px-3 py-2 transition-colors"
            >
              {loginLabel}
            </a>
            <a
              href={ctaHref}
              className="rounded-full bg-primary-700 hover:bg-primary-800 text-white text-sm font-bold px-5 py-2.5 transition-colors"
            >
              {config.cta}
            </a>
          </div>
        </div>
      </header>

      {/* hero */}
      <section className="relative overflow-hidden bg-primary-50/60 dark:bg-neutral-900">
        <div className="max-w-3xl mx-auto px-5 pt-16 pb-14 md:pt-24 md:pb-20 text-center">
          <motion.h1
            {...fadeUp}
            className="font-serif text-[38px] leading-[1.12] sm:text-5xl sm:leading-[1.12] md:text-6xl md:leading-[1.12] pb-1 tracking-tight text-neutral-900 dark:text-neutral-50"
          >
            {config.headline}
          </motion.h1>
          <motion.p
            {...fadeUp}
            transition={{ duration: 0.45, delay: 0.08 }}
            className="text-lg md:text-xl text-neutral-600 dark:text-neutral-300 mt-5 max-w-xl mx-auto"
          >
            {config.subline}
          </motion.p>
          <motion.div {...fadeUp} transition={{ duration: 0.45, delay: 0.16 }} className="mt-8">
            <Cta href={ctaHref} label={config.cta} sub={config.ctaSub} />
          </motion.div>
        </div>

        {/* One row always, bleeding off both edges on narrow screens: four
            stacked polaroids made the mobile hero taller than the fold. */}
        {config.heroImages && config.heroImages.length > 0 && (
          <div className="pb-14 overflow-hidden">
            <div className="max-w-5xl mx-auto flex justify-center items-center gap-3 md:gap-5 flex-nowrap px-5">
              {config.heroImages.slice(0, 4).map((src, i) => (
                <motion.div
                  key={i}
                  initial={reduceMotion ? false : { opacity: 0, y: 24, rotate: 0 }}
                  whileInView={{ opacity: 1, y: 0, rotate: [-4, 2.5, -2, 3.5][i % 4] }}
                  viewport={{ once: true }}
                  transition={reduceMotion ? { duration: 0 } : { duration: 0.5, delay: i * 0.08 }}
                  className="w-[38%] shrink-0 md:w-1/4 bg-white dark:bg-neutral-100 p-1.5 pb-5 md:p-2 md:pb-6 rounded-sm shadow-xl shadow-black/20"
                >
                  <img
                    src={src}
                    alt=""
                    loading="lazy"
                    className="w-full aspect-[4/5] object-cover"
                  />
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* social proof, its own moment rather than a micro-strip in the hero */}
      <div className="bg-white dark:bg-neutral-900 py-6 border-b border-neutral-200 dark:border-neutral-800">
        <ProofStrip className="text-neutral-700 dark:text-neutral-300" />
      </div>

      {/* outings near you */}
      {config.coverage && (
        <section className="border-y border-neutral-200 dark:border-neutral-800 bg-primary-50/50 dark:bg-[#0D0D0D]">
          <div className="max-w-2xl mx-auto px-5 py-16 md:py-24 text-center">
            <p className="text-xs uppercase tracking-widest text-primary-700 dark:text-primary-400 font-semibold mb-5">
              <MapPin className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />
              {config.coverage.title.replace("{cities}", String(cities.length || 25))}
            </p>

            <motion.div {...fadeUp} className="mb-8">
              <SectionTitle>{config.coverage.headline}</SectionTitle>
              {config.coverage.subline && (
                <p className="text-lg text-neutral-600 dark:text-neutral-300 mt-4 max-w-lg mx-auto">
                  {config.coverage.subline}
                </p>
              )}
            </motion.div>

            <label className="sr-only" htmlFor="longform-city">
              Your city
            </label>
            <select
              id="longform-city"
              value={selectedCityId ?? ""}
              onChange={(e) => setPickedCityId(e.target.value)}
              className="rounded-full border border-neutral-300 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 text-neutral-800 dark:text-neutral-100 font-semibold text-sm px-5 py-2.5 max-w-full"
            >
              {citiesLoading && <option value="">{tRaw("longform.band.findingCity")}</option>}
              {!citiesLoading && !selectedCityId && (
                <option value="">{tRaw("longform.band.selectCity")}</option>
              )}
              {cities.map((city) => (
                <option key={city.id} value={city.id}>
                  {city.emoji ? `${city.emoji} ` : ""}
                  {city.name}
                </option>
              ))}
            </select>

            <div className="mt-6 flex flex-col gap-3 text-left">
              {cityOutings.length > 0 ? (
                cityOutings.map((outing, i) => {
                  const type = TYPE_META[outing.type] ?? TYPE_META.dinner;
                  return (
                    <motion.a
                      key={`${outing.date}-${outing.time}`}
                      {...fadeUp}
                      transition={{ duration: 0.4, delay: i * 0.05 }}
                      href={bookHref}
                      className="group rounded-2xl border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 hover:border-primary-500 hover:shadow-md p-5 transition-all flex items-center gap-4"
                    >
                      <span
                        className={`shrink-0 w-12 h-12 rounded-full inline-flex items-center justify-center ${type.medallion}`}
                        aria-hidden
                      >
                        <type.Icon className="w-6 h-6" />
                      </span>
                      <span className="min-w-0">
                        <span className="flex items-center gap-2 mb-1.5 flex-wrap">
                          <span
                            className={`rounded-full text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 ${type.chip}`}
                          >
                            {tRaw(type.labelKey)}
                          </span>
                          {config.groupSizeBadge && (
                            <span className="rounded-full bg-neutral-200/70 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300 text-[11px] font-semibold px-2.5 py-1 inline-flex items-center gap-1">
                              <Users className="w-3 h-3" />
                              {config.groupSizeBadge}
                            </span>
                          )}
                        </span>
                        <span className="block font-semibold font-Poppins text-lg text-neutral-900 dark:text-neutral-50 group-hover:text-primary-500 transition-colors">
                          {formatOutingDate(outing.date, language)}
                        </span>
                        <span className="block text-sm text-neutral-500 dark:text-neutral-400 mt-0.5 flex items-center gap-1.5">
                          <Clock className="w-3.5 h-3.5" />
                          {formatOutingTime(outing.time, language)}
                          {selectedCity ? ` \u00b7 ${selectedCity.name}` : ""}
                        </span>
                      </span>
                    </motion.a>
                  );
                })
              ) : (
                <p className="text-center text-neutral-500 dark:text-neutral-400 py-4">
                  {outingsLoading ? tRaw("longform.band.loadingDates") : config.coverage.emptyLabel}
                </p>
              )}
            </div>

            <div className="mt-8">
              <Cta href={bookHref} label={config.coverage.cta} size="md" />
            </div>
          </div>
        </section>
      )}

      {/* how it works */}
      <section className="bg-neutral-50 dark:bg-[#0D0D0D]">
        <div className="max-w-4xl mx-auto px-5 py-16 md:py-24">
          <motion.div {...fadeUp}>
            <SectionTitle>{config.stepsTitle}</SectionTitle>
          </motion.div>
          {/* Timeline, not cards: the steps are a sequence, and this page
              already spends four other sections on card grids. */}
          <ol className="mt-12 max-w-2xl mx-auto">
            {config.steps.map((step, i) => (
              <motion.li
                key={i}
                {...fadeUp}
                transition={{ duration: 0.45, delay: i * 0.06 }}
                className="relative flex gap-5 pb-9 last:pb-0"
              >
                {i < config.steps.length - 1 && (
                  <span
                    className="absolute left-[17px] top-10 bottom-0 w-px bg-neutral-300 dark:bg-neutral-700"
                    aria-hidden
                  />
                )}
                <span className="relative z-10 shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full bg-primary-500/10 text-primary-700 dark:text-primary-400 font-bold text-sm">
                  {i + 1}
                </span>
                <div className="pt-1">
                  <h3 className="text-lg font-semibold font-Poppins text-neutral-900 dark:text-neutral-50 mb-1">
                    {step.title}
                  </h3>
                  <p className="text-neutral-600 dark:text-neutral-400 text-[15px] leading-relaxed">
                    {step.body}
                  </p>
                </div>
              </motion.li>
            ))}
          </ol>

          <div className="mt-12">
            <Cta href={ctaHref} label={config.cta} sub={config.ctaSub} />
          </div>
        </div>
      </section>

      {/* self-qualifier */}
      {config.qualifier && (
        <section className="bg-white dark:bg-neutral-900">
          <div className="max-w-2xl mx-auto px-5 py-16 md:py-20 text-center">
            <motion.div {...fadeUp}>
              <SectionTitle>{config.qualifier.question}</SectionTitle>
            </motion.div>
            {qualified === false ? (
              <p className="mt-8 text-neutral-600 dark:text-neutral-400">
                {config.qualifier.noReply}
              </p>
            ) : (
              <div className="mt-8 flex items-center justify-center gap-3">
                <a
                  href={ctaHref}
                  className="rounded-full bg-primary-700 hover:bg-primary-800 text-white font-bold text-lg px-10 py-3.5 transition-colors shadow-lg shadow-primary-700/30"
                >
                  {config.qualifier.yes}
                </a>
                <button
                  type="button"
                  onClick={() => setQualified(false)}
                  className="rounded-full border border-neutral-300 dark:border-neutral-700 text-neutral-600 dark:text-neutral-300 font-semibold px-10 py-3.5 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
                >
                  {config.qualifier.no}
                </button>
              </div>
            )}
            <p className="mt-6 text-sm text-neutral-500 dark:text-neutral-400">
              <a href={APP_URL} className="underline hover:text-primary-500 transition-colors">
                {loginLabel}
              </a>
            </p>
          </div>
        </section>
      )}

      {/* formats */}
      <section className="bg-neutral-50 dark:bg-[#0D0D0D]">
        <div className="max-w-4xl mx-auto px-5 py-16 md:py-24">
          <motion.div {...fadeUp}>
            <SectionTitle>{config.formatsTitle}</SectionTitle>
            <p className="text-center text-neutral-600 dark:text-neutral-300 mt-5 max-w-2xl mx-auto leading-relaxed">
              {config.formatsIntro}
            </p>
          </motion.div>
          <div className="grid sm:grid-cols-2 gap-5 mt-12">
            {config.formats.map((format, i) => (
              <motion.div
                key={i}
                {...fadeUp}
                transition={{ duration: 0.45, delay: i * 0.06 }}
                className="rounded-2xl bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 p-6"
              >
                <h3 className="font-serif text-2xl leading-[1.15] pb-0.5 text-neutral-900 dark:text-neutral-50 mb-2">
                  {format.name}
                </h3>
                <p className="text-neutral-600 dark:text-neutral-400 text-[15px] leading-relaxed">
                  {format.body}
                </p>
              </motion.div>
            ))}
          </div>
          <div className="mt-12">
            <Cta href={ctaHref} label={config.cta} size="md" />
          </div>
        </div>
      </section>

      {/* founder story */}
      {config.story && (
        <section className="bg-grape-700 dark:bg-grape-800 text-white">
          <div className="max-w-3xl mx-auto px-5 py-16 md:py-24 text-center">
            <motion.h2
              {...fadeUp}
              className="font-serif text-3xl leading-[1.15] md:text-[42px] md:leading-[1.15] pb-1 tracking-tight"
            >
              {config.story.headline}
            </motion.h2>
            <div className="mt-7 flex flex-col gap-4 text-left max-w-xl mx-auto">
              {config.story.body.map((para, i) => (
                <motion.p
                  key={i}
                  {...fadeUp}
                  transition={{ duration: 0.45, delay: i * 0.06 }}
                  className="text-white/80 leading-relaxed"
                >
                  {para}
                </motion.p>
              ))}
            </div>
            <div className="mt-10">
              <Cta href={ctaHref} label={config.cta} size="md" />
            </div>
          </div>
        </section>
      )}

      {/* benefits + not for you */}
      <section className="bg-white dark:bg-neutral-900">
        <div className="max-w-4xl mx-auto px-5 py-16 md:py-24">
          <motion.div {...fadeUp}>
            <SectionTitle>{config.benefitsTitle}</SectionTitle>
          </motion.div>
          <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-4 mt-10 max-w-2xl mx-auto">
            {config.benefits.map((benefit, i) => (
              <motion.li
                key={i}
                {...fadeUp}
                transition={{ duration: 0.4, delay: i * 0.04 }}
                className="flex items-start gap-3"
              >
                <Check className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
                <span className="text-neutral-700 dark:text-neutral-300">{benefit}</span>
              </motion.li>
            ))}
          </ul>

          <motion.div
            {...fadeUp}
            className="mt-14 rounded-2xl border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 p-7 max-w-2xl mx-auto"
          >
            <h3 className="text-lg font-semibold font-Poppins text-neutral-900 dark:text-neutral-50 mb-4">
              {config.notForYouTitle}
            </h3>
            <ul className="flex flex-col gap-3">
              {config.notForYou.map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <X className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                  <span className="text-neutral-700 dark:text-neutral-300">{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* why it works */}
      <section className="bg-neutral-50 dark:bg-[#0D0D0D]">
        <div className="max-w-4xl mx-auto px-5 py-16 md:py-24">
          <motion.div {...fadeUp}>
            <SectionTitle>{config.whyTitle}</SectionTitle>
          </motion.div>
          {/* Divided list, not a 3-up card grid: six near-equivalent claims in
              identical cards read as filler, and equal 3-col feature cards are
              the most templated pattern on the web. */}
          <dl className="mt-10 grid sm:grid-cols-2 gap-x-10 max-w-3xl mx-auto">
            {config.why.map((item, i) => (
              <motion.div
                key={i}
                {...fadeUp}
                transition={{ duration: 0.45, delay: i * 0.05 }}
                className="py-5 border-t border-neutral-200 dark:border-neutral-800"
              >
                <dt className="text-base font-semibold font-Poppins text-neutral-900 dark:text-neutral-50 mb-1">
                  {item.title}
                </dt>
                <dd className="text-neutral-600 dark:text-neutral-400 text-[15px] leading-relaxed">
                  {item.body}
                </dd>
              </motion.div>
            ))}
          </dl>

          {config.stats && config.stats.length > 0 && (
            <motion.div
              {...fadeUp}
              className="mt-12 grid grid-cols-3 gap-4 rounded-2xl bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 p-7"
            >
              {config.stats.map((stat, i) => (
                <div key={i} className="text-center">
                  <p className="font-serif text-3xl leading-[1.15] md:text-4xl md:leading-[1.15] pb-0.5 text-primary-600 dark:text-primary-400">{stat.value}</p>
                  <p className="text-xs md:text-sm text-neutral-500 dark:text-neutral-400 mt-1.5 leading-snug">
                    {stat.label}
                  </p>
                </div>
              ))}
            </motion.div>
          )}

          <div className="mt-12">
            <Cta href={ctaHref} label={config.cta} sub={config.ctaSub} />
          </div>
        </div>
      </section>

      {/* testimonials */}
      {config.testimonials && config.testimonials.items.length > 0 && (
        <section className="bg-white dark:bg-neutral-900">
          <div className="max-w-4xl mx-auto px-5 py-16 md:py-24">
            <motion.div {...fadeUp}>
              <SectionTitle>{config.testimonials.title}</SectionTitle>
            </motion.div>
            <div
              className={`grid gap-5 mt-12 ${
                config.testimonials.items.length === 2
                  ? "md:grid-cols-2 max-w-3xl mx-auto"
                  : "md:grid-cols-3"
              }`}
            >
              {config.testimonials.items.map((item, i) => (
                <motion.figure
                  key={i}
                  {...fadeUp}
                  transition={{ duration: 0.45, delay: i * 0.06 }}
                  className="rounded-2xl bg-neutral-50 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 p-6 flex flex-col"
                >
                  <div className="flex gap-0.5 mb-3" aria-label="5 out of 5">
                    {Array.from({ length: 5 }).map((_, star) => (
                      <Star key={star} className="w-4 h-4 fill-amber-600 text-amber-600 dark:fill-amber-400 dark:text-amber-400" />
                    ))}
                  </div>
                  <blockquote className="text-[15px] leading-relaxed text-neutral-700 dark:text-neutral-300 flex-1">
                    {item.quote}
                  </blockquote>
                  <figcaption className="text-sm font-semibold text-neutral-500 dark:text-neutral-400 mt-4">
                    {item.name}, {item.age}
                    <span className="font-normal"> · {item.city}</span>
                  </figcaption>
                </motion.figure>
              ))}
            </div>
            <div className="mt-10">
              <ProofStrip className="text-neutral-600 dark:text-neutral-300" />
            </div>
          </div>
        </section>
      )}

      {/* safety */}
      <section className="bg-white dark:bg-neutral-900">
        <div className="max-w-3xl mx-auto px-5 py-16 md:py-24">
          <motion.div {...fadeUp}>
            <SectionTitle>{config.safetyTitle}</SectionTitle>
          </motion.div>
          <div className="grid sm:grid-cols-2 gap-6 mt-12">
            {config.safety.map((item, i) => {
              const Icon = [ShieldCheck, Users, Lock, LifeBuoy][i % 4];
              return (
                <motion.div
                  key={i}
                  {...fadeUp}
                  transition={{ duration: 0.45, delay: i * 0.05 }}
                  className="flex gap-4"
                >
                  <Icon className="w-6 h-6 text-primary-500 shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-lg font-semibold font-Poppins text-neutral-900 dark:text-neutral-50 mb-1">
                      {item.title}
                    </h3>
                    <p className="text-neutral-600 dark:text-neutral-400 text-[15px] leading-relaxed">
                      {item.body}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-neutral-50 dark:bg-[#0D0D0D]">
        <div className="max-w-2xl mx-auto px-5 py-16 md:py-24">
          <motion.div {...fadeUp}>
            <SectionTitle>{config.faqTitle}</SectionTitle>
          </motion.div>
          <div className="flex flex-col gap-3 mt-10">
            {config.faq.map((item, i) => (
              <Expandable key={i} title={item.q}>
                {item.a}
              </Expandable>
            ))}
          </div>
        </div>
      </section>

      {/* emotional close */}
      <section className="bg-neutral-900 dark:bg-black text-white">
        <div className="max-w-2xl mx-auto px-5 py-20 md:py-28 text-center">
          <motion.h2
            {...fadeUp}
            className="font-serif text-3xl leading-[1.15] md:text-5xl md:leading-[1.15] pb-1 tracking-tight"
          >
            {config.close.headline}
          </motion.h2>
          <motion.p
            {...fadeUp}
            transition={{ duration: 0.45, delay: 0.08 }}
            className="font-serif text-2xl md:text-3xl text-primary-400 mt-3"
          >
            {config.close.sub}
          </motion.p>
          <div className="mt-8 flex flex-col gap-4 text-left">
            {config.close.body.map((para, i) => (
              <motion.p
                key={i}
                {...fadeUp}
                transition={{ duration: 0.45, delay: i * 0.05 }}
                className="text-white/75 leading-relaxed"
              >
                {para}
              </motion.p>
            ))}
          </div>
          <div className="mt-12">
            <Cta href={ctaHref} label={config.cta} sub={config.ctaSub} />
          </div>
          <div className="mt-10">
            <ProofStrip className="text-white/70" />
          </div>
        </div>
      </section>

      {/* footer */}
      <footer className="bg-white dark:bg-neutral-900 py-10 text-center text-xs text-neutral-600 dark:text-neutral-400">
        <p className="flex items-center justify-center gap-1.5 mb-2">
          <span className="inline-block w-2.5 h-2.5 rounded-sm bg-gradient-to-br from-red-500 via-yellow-400 to-violet-500" />
          {tRaw(config.ownedLabel === "lgbtq" ? "footer.proudlyLgbtqOwned" : "footer.proudlyGayOwned")}
        </p>
        <p>
          <Link href="/terms" className="underline hover:text-primary-500 transition-colors">
            {tRaw("footer.terms")}
          </Link>
          {" · "}
          <Link href="/privacy" className="underline hover:text-primary-500 transition-colors">
            {tRaw("footer.privacy")}
          </Link>
        </p>
      </footer>

      <StickyCta href={ctaHref} label={config.cta} />
    </div>
  );
}

const PHOTO_BASE =
  "https://firebasestorage.googleapis.com/v0/b/gayborhood-471323.firebasestorage.app/o/creative-assets%2Fphoto-library%2F";

/**
 * Hero photos. Every entry is an APPROVED row in the marketing photo library
 * (`creative_photos.status = 'approved'`), cited by its library name so the
 * source is auditable. Never point this at a pending_review asset.
 */
const HERO_MEN = [
  `${PHOTO_BASE}c9c062a5934de0b1.jpg?alt=media&token=f84b350c-08b7-4d70-836a-e2714b8203db`, // men30s-dinner-group-selfie-ba5ba7
  `${PHOTO_BASE}11d0a7665eaadf16.jpg?alt=media&token=da924517-184a-448b-84f7-ba665db4c37d`, // men2040-five-long-table
  `${PHOTO_BASE}fdad7d6567b1ced5.jpg?alt=media&token=7dd56637-589c-4e64-baf8-f5815a782558`, // men2040-trio-real-laugh
  `${PHOTO_BASE}bd822fde714f34e0.jpg?alt=media&token=39e09780-505b-48c2-a432-2d9053f380c8`, // men30s-dinner-terrace-golden-trio
];

const HERO_MEN_50P = [
  `${PHOTO_BASE}502333bdc5f05a02.jpg?alt=media&token=5810d65c-86b0-41f2-b84f-fd4b500042f0`, // men50p2-four-long-table
  `${PHOTO_BASE}5414a81081eba550.jpg?alt=media&token=0af94ec2-9c22-4f0d-8a69-7b19cd9643b5`, // men50-dinner-string-lights-a23a43
  `${PHOTO_BASE}9886732833a57f79.jpg?alt=media&token=0eff7dd2-9cc3-41eb-ac65-6653233a3802`, // men50-dinner-courtyard-dusk-duo
  `${PHOTO_BASE}e6c3faab46955032.jpg?alt=media&token=db6e3c20-1448-4f28-bf7a-20302b7b5189`, // men50p3-duo-carafe-pour
];

/**
 * The photo library's oldest sapphic casting is `sap4050`, not a 50+ segment.
 * These are the right faces available today; a true 50+ sapphic shoot is a gap.
 */
const HERO_SAPPHIC_50P = [
  // Night flash alternating with daylight, same rhythm as the under-50 set.
  // The two flash frames were generated for this page; the library's oldest
  // sapphic audience value is `sap4050`, so that is what they are filed under
  // even though the casting reads late 50s and 60s.
  `${PHOTO_BASE}43bacb2a00ea79b7.jpg?alt=media&token=e258e881-d2d4-4201-9e41-651ff6a9011b`, // sap4050-dinner-flash-night-fourtop
  `${PHOTO_BASE}599fb05ca758c4d7.jpg?alt=media&token=1c8b545f-42c9-4886-a895-d3e29c36d960`, // sap4050-dinner-garden-courtyard-30f57a
  `${PHOTO_BASE}2b325600094b7627.jpg?alt=media&token=e90e1b6f-6c9d-4a05-89a6-a8584331a682`, // sap4050-dinner-flash-night-toast
  `${PHOTO_BASE}3d5e72043c4191ff.jpg?alt=media&token=b59d8ea2-ecf1-4c82-8a16-cafe92044942`, // sap4050-coffee-terrace-c98dbc
];

const HERO_SAPPHIC = [
  // Cast for the under-50 page, alternating night flash and daylight so the
  // polaroid row has rhythm rather than two dark frames side by side.
  //
  // The two flash-night frames were generated to fill a real gap: before them,
  // all 21 sapphic rows were day/evening/golden-evening, and the only Black
  // representation sat in a cafe group that read 30s-40s.
  //
  // Excluded on purpose: `sap2039-dinner-table-e18795` is two grey-haired women
  // despite its tag (it belongs on the 50+ page), and `-9b29e8` / `-2d62d6` /
  // `-park-bench` / `-candle-table` are all-white pairs. The library's audience
  // tag is casting intent, not what the render shows: judge the pixels.
  `${PHOTO_BASE}13dc3308876b9452.jpg?alt=media&token=5ca67cc0-bd5a-47d0-829e-2172fc345805`, // sap2039-dinner-flash-night-booth
  `${PHOTO_BASE}873c28ff1f4e6f71.jpg?alt=media&token=f09f6de4-3894-4335-ac02-42531cf2a1bd`, // sap2039-coffee-table-f3799b
  `${PHOTO_BASE}f63a20af4bb75b8a.jpg?alt=media&token=04d63a55-ee1c-4f65-bdb7-19a9df764c8c`, // sap2039-dinner-flash-night-clink
  `${PHOTO_BASE}694df6e5c6f16f49.jpg?alt=media&token=8d9cdfdf-3f41-4493-8dce-3c80bbc109ef`, // sap2039-coffee-walking-833196
];

/**
 * Real numbers only. Computed 2026-08-24 from 5,462 post-Outing surveys
 * (`post_dinner_feedback`): felt_welcomed = yes → 94.6%, future_attendance =
 * yes → 88.7%. Re-derive before quoting these on a new page; do not round up.
 */
const REAL_STATS: LongformStat[] = [
  { value: "95%", label: "Felt welcomed at their table" },
  { value: "89%", label: "Would go to another Outing" },
  { value: "5,400+", label: "Outings rated by members" },
];

/**
 * Real member quotes. Text is the `payment.testimonials` set in the locale
 * files; attribution matches `data/paywallTestimonials.ts`. Never invent one.
 */
/**
 * Younger men. Only two are usable together: the pool's third quote (Marcus,
 * 27) closes on "text chain", the same phrase as Diego's, and two of three
 * quotes sharing a phrase reads as one author rather than three members.
 */
const TESTIMONIALS_MEN: LongformTestimonial[] = [
  {
    quote:
      "First 10 minutes were a little stiff, not gonna lie. Then somebody made a joke that landed and the whole table opened up. Four of us ended up at a bar after and I got home at midnight.",
    name: "Tyler",
    age: 24,
    city: "DC",
  },
  {
    quote:
      "I've lived in Chicago for 6 years and made zero gay friends. I went to one Outclose. Now I have a brunch text chain. Make of that what you will.",
    name: "Diego",
    age: 31,
    city: "Chicago",
  },
];

/**
 * Older men. This is the paywall's M40 segment, so it runs 44-52 rather than a
 * strict 50+. David is the only member here actually past 50.
 */
const TESTIMONIALS_MEN_50P: LongformTestimonial[] = [
  {
    quote:
      "After my divorce I needed actual friends, not just guys to text at 11pm. This is the first thing in two years that's helped. The food was also really good which I wasn't expecting.",
    name: "Anthony",
    age: 44,
    city: "NYC",
  },
  {
    quote:
      "I came out at 39. Spent two years feeling like everyone else got their friends in college and I was 20 years late. The first dinner I went to, I sat next to a guy who came out at 41. So.",
    name: "James",
    age: 47,
    city: "Los Angeles",
  },
  {
    quote:
      "Moved to SF for my husband's job last year. Rebuilding gay friendships at 52 is brutal. My first dinner had two other guys who'd just moved here too. We have plans next Sunday.",
    name: "David",
    age: 52,
    city: "San Francisco",
  },
];

/** The entire sapphic pool is three quotes, ages 26-46. Renee is the oldest. */
const TESTIMONIALS_SAPPHIC: LongformTestimonial[] = [
  {
    quote:
      "I wanted to meet other lesbians without it being a dating thing. That's actually what this is. I have a hiking plan with two of them next weekend.",
    name: "Mei",
    age: 26,
    city: "LA",
  },
  {
    quote:
      "Listen. I have been trying to find my people in this city for like four years. Two dinners. TWO. I am stunned.",
    name: "Jordan",
    age: 32,
    city: "Toronto",
  },
  {
    quote:
      "I came out late. I sat in my car for a minute before going in because I was nervous. Then I had a really nice time. Both can be true.",
    name: "Renée",
    age: 46,
    city: "Chicago",
  },
];

/** Example config, rendered at /tpl/longform and documented for MCP authors. */
export const LONGFORM_MEN: LongformConfig = {
  posthogEvent: "men_longform_landing",
  audienceParam: "m",
  headline: "Meet gay men you'll actually want to see again",
  subline:
    "Take a quiz, get matched with a small group, show up to a table we already booked.",
  cta: "Take the quiz",
  ctaSub: "Five minutes. We build your table from it.",

  heroImages: HERO_MEN,

  coverage: {
    title: "Outings in {cities} cities worldwide",
    headline: "There's a table near you this week",
    subline: "Real dates, in your city, open right now. Pick one and we'll build the table around you.",
    emptyLabel: "No dates open in your city just yet. Take the quiz and we'll tell you the moment one is.",
    cta: "Book your spot",
  },
  groupSizeBadge: "4 to 8 people",

  stepsTitle: "No swiping, no ghosting, no awkward hovering by the bar. Just show up.",
  steps: [
    {
      title: "Take the matching quiz",
      body: "A few minutes on who you are, what you're into, and how you actually socialize. That's what the matching runs on.",
    },
    {
      title: "Pick an Outing",
      body: "Dinner, brunch or coffee, depending on your city. Pick the date that works and we handle the venue.",
    },
    {
      title: "Show up and have a good time",
      body: "We match you with a group of gay men who fit, then send you the details. All you do is turn up.",
    },
    {
      title: "Stay in touch after",
      body: "Afterwards you pick who you'd like to see again. When it's mutual, we open the chat.",
    },
  ],

  qualifier: {
    question: "Ready to meet gay men in your city?",
    yes: "Yes",
    no: "Not right now",
    noReply: "Totally fair. We'll be here when the timing is better.",
  },

  formatsTitle: "A small group that feels right from the first ten minutes",
  formatsIntro:
    "Nobody arrives looking for a performance. They want a good conversation, a relaxed room, and the chance to meet people who get it. No pressure, no expectations.",
  formats: [
    {
      name: "Dinners",
      body: "A long, easy meal with gay men matched to you on age, interests and personality. The format we run most often, in every city.",
    },
    {
      name: "Brunch",
      body: "The same matched table, earlier and lighter. In select cities, with more added as they open.",
    },
    {
      name: "Coffee",
      body: "The lowest-commitment way in. Select neighborhoods, no reservation, you find each other in the table chat.",
    },
    {
      name: "Your table, not a room",
      body: "Every format is one small group at one table. Big events are easy to disappear in. A table is not.",
    },
  ],

  story: {
    headline: "The places where gay friendships used to start are disappearing.",
    body: [
      "Outclose started because the third spaces went first. The bars closed, the bookstores closed, and what replaced them was an app built for something else entirely.",
      "So we built the opposite. Light on tech, heavy on hospitality. A quiz, a matching algorithm, a vetted venue, and a small group of gay men who all showed up for the same reason.",
      "Turns out the thing that was missing was never more people. It was one table, on a night that was already booked.",
    ],
  },

  benefitsTitle: "What you actually get out of it",
  benefits: [
    "Meet gay men at a similar life stage",
    "Build a circle in your own city",
    "Feel less like the new guy going out",
    "Conversations that get past small talk",
    "Venues you'd go back to on your own",
    "A standing reason to leave the house",
  ],

  notForYouTitle: "This probably isn't for you if",
  notForYou: [
    "You can't commit to actually showing up",
    "You only want to meet people exactly like you",
    "You're looking for a dating app",
  ],

  whyTitle: "Why a matched table works",
  why: [
    {
      title: "Matched, not random",
      body: "Age, life stage, values, personality and social energy. Talkers get seated with listeners on purpose.",
    },
    {
      title: "Gay men at the table",
      body: "Book a men's table and that's who's there. Being gay is the baseline, not the topic.",
    },
    {
      title: "Venues we vet",
      body: "Gay-owned or gay-welcoming places, chosen so a group can hear each other talk.",
    },
    {
      title: "Every week",
      body: "This isn't a one-off event you have to catch. There's another Outing next week, and the one after.",
    },
    {
      title: "The plan is already made",
      body: "Booked venue, set time, group sorted. The part where nothing gets planned is handled.",
    },
    {
      title: "It keeps going after",
      body: "Mutual picks unlock contacts, so a good night can become a group chat instead of a nice memory.",
    },
  ],

  stats: REAL_STATS,

  testimonials: {
    title: "What members say after their first table",
    items: TESTIMONIALS_MEN,
  },

  safetyTitle: "How we keep it safe",
  safety: [
    {
      title: "Always public, always a group",
      body: "Every Outing is a small group at a vetted venue. Never a one-on-one, never someone's apartment.",
    },
    {
      title: "Everyone is verified",
      body: "Members sign up with a real account and pay for their seat. It takes effort to be at the table.",
    },
    {
      title: "Your details stay yours",
      body: "Contact details only open when you both choose each other after the Outing. Nothing is shared by default.",
    },
    {
      title: "We're reachable",
      body: "Bad behavior gets someone removed. If anything felt off, tell us and we'll deal with it.",
    },
  ],

  faqTitle: "Questions people ask first",
  faq: [
    {
      q: "Is this a dating service?",
      a: "No. It's built for friendship. Sometimes sparks fly anyway. We just make the intro, and most people come with an open mind.",
    },
    {
      q: "How are the groups matched?",
      a: "From your quiz. Age and life stage, interests, values, personality and social energy, plus complementary traits so the conversation has somewhere to go.",
    },
    {
      q: "What does it cost?",
      a: "$15.99 per Outing, or $19.99 a month for weekly Outings plus perks. Food and drink at the venue are on you, like any night out.",
    },
    {
      q: "When do I get the details?",
      a: "Your table and venue land the day before, along with the group chat. For coffee there's no reservation, so you sort out where to meet in the chat.",
    },
    {
      q: "What if I don't click with the group?",
      a: "It happens. You mark who you'd want to see again, nothing awkward gets forced, and you take a different table next time.",
    },
    {
      q: "Can I bring a friend?",
      a: "The matching works because everyone arrives on their own footing. Send them the quiz and they can book their own table.",
    },
    {
      q: "What about dietary requirements?",
      a: "You tell us in the quiz and we factor it into the venue we book for your table.",
    },
    {
      q: "Which cities are you in?",
      a: "Cities across the US, Canada, the UK, France, Germany, Belgium and Australia, with new neighborhoods opening regularly. The picker above shows every city we currently run in.",
    },
  ],

  close: {
    headline: "Making gay friends as an adult shouldn't be this hard.",
    sub: "But it is.",
    body: [
      "The apps are pointed at something else. The bars are loud and everyone came with their group. The big events are fun and you still go home without a single new number.",
      "So people stop trying, and call it being busy.",
      "Here's the alternative. One table. A few hours. A small group of gay men who took the same quiz and booked the same night, in a place we already picked for you.",
      "You don't have to organize anything. You just have to show up.",
    ],
  },

  ownedLabel: "gay",
};

export const LONGFORM_SAPPHIC: LongformConfig = {
  ...LONGFORM_MEN,
  posthogEvent: "sapphic_longform_landing",
  audienceParam: "s",
  headline: "Meet sapphic women you'll actually want to see again",
  subline:
    "Take a quiz, get matched with a small group, show up to a table we already booked.",

  heroImages: HERO_SAPPHIC,

  testimonials: {
    title: "What members say after their first table",
    items: TESTIMONIALS_SAPPHIC,
  },

  stepsTitle: "No apps, no group chat that never turns into plans. Just show up.",
  steps: [
    {
      title: "Take the matching quiz",
      body: "A few minutes on who you are, what you're into, and how you actually socialize. That's what the matching runs on.",
    },
    {
      title: "Pick an Outing",
      body: "Dinner, brunch or coffee, depending on your city. Pick the date that works and we handle the venue.",
    },
    {
      title: "Show up and have a good time",
      body: "We match you with a group of sapphic women who fit, then send you the details. All you do is turn up.",
    },
    {
      title: "Stay in touch after",
      body: "Afterwards you pick who you'd like to see again. When it's mutual, we open the chat.",
    },
  ],

  qualifier: {
    question: "Ready to meet sapphic women in your city?",
    yes: "Yes",
    no: "Not right now",
    noReply: "Totally fair. We'll be here when the timing is better.",
  },

  formatsTitle: "A small group that feels right from the first ten minutes",
  formatsIntro:
    "Nobody arrives looking for a performance. They want a good conversation, a relaxed room, and the chance to meet people who get it. No pressure, no expectations.",
  formats: [
    {
      name: "Dinners",
      body: "A long, easy meal with sapphic women matched to you on age, interests and personality. The format we run most often, in every city.",
    },
    {
      name: "Brunch",
      body: "The same matched table, earlier and lighter. In select cities, with more added as they open.",
    },
    {
      name: "Coffee",
      body: "The lowest-commitment way in. Select neighborhoods, no reservation, you find each other in the table chat.",
    },
    {
      name: "Your table, not a room",
      body: "Every format is one small group at one table. Big events are easy to disappear in. A table is not.",
    },
  ],

  story: {
    headline: "Most cities are down to one sapphic space. Some are down to none.",
    body: [
      "Outclose started because the third spaces went first. The bars closed, the bookstores closed, and what replaced them was an app built for something else entirely.",
      "So we built the opposite. Light on tech, heavy on hospitality. A quiz, a matching algorithm, a vetted venue, and a small group of sapphic women who all showed up for the same reason.",
      "Turns out the thing that was missing was never more people. It was one table, on a night that was already booked.",
    ],
  },

  benefits: [
    "Meet sapphic women at a similar life stage",
    "Build a circle in your own city",
    "Feel less like the new one in the room",
    "Conversations that get past small talk",
    "Venues you'd go back to on your own",
    "A standing reason to leave the house",
  ],

  notForYou: [
    "You can't commit to actually showing up",
    "You only want to meet people exactly like you",
    "You're looking for a dating app",
  ],

  why: [
    {
      title: "Matched, not random",
      body: "Age, life stage, values, personality and social energy. Talkers get seated with listeners on purpose.",
    },
    {
      title: "A sapphic table",
      body: "Book a sapphic table and that's who's there. No explaining yourself, no being the only one.",
    },
    {
      title: "Venues we vet",
      body: "Welcoming places, chosen so a group can actually hear each other talk.",
    },
    {
      title: "Every week",
      body: "This isn't a one-off event you have to catch. There's another Outing next week, and the one after.",
    },
    {
      title: "The plan is already made",
      body: "Booked venue, set time, group sorted. The part where nothing gets planned is handled.",
    },
    {
      title: "It keeps going after",
      body: "Mutual picks unlock contacts, so a good night can become a group chat instead of a nice memory.",
    },
  ],

  safety: [
    {
      title: "Always public, always a group",
      body: "Every Outing is a small group at a vetted venue. Never a one-on-one, never someone's apartment.",
    },
    {
      title: "Everyone is verified",
      body: "Members sign up with a real account and pay for their seat. It takes effort to be at the table.",
    },
    {
      title: "Your details stay yours",
      body: "Contact details only open when you both choose each other after the Outing. Nothing is shared by default.",
    },
    {
      title: "We're reachable",
      body: "Bad behavior gets someone removed. If anything felt off, tell us and we'll deal with it.",
    },
  ],

  close: {
    headline: "Making sapphic friends as an adult shouldn't be this hard.",
    sub: "But it is.",
    body: [
      "The apps are pointed at something else. The one bar left is the same crowd on the same nights. The big events are fun and you still go home without a single new number.",
      "So people stop trying, and call it being busy.",
      "Here's the alternative. One table. A few hours. A small group of sapphic women who took the same quiz and booked the same night, in a place we already picked for you.",
      "You don't have to organize anything. You just have to show up.",
    ],
  },
};

/**
 * Men 50+. Same mechanism, different life stage: the fear is not "will I fit
 * in", it is "is it too late to start". Photos are the men50 casting; quotes
 * are the paywall's 40+ segment.
 */
export const LONGFORM_MEN_50P: LongformConfig = {
  ...LONGFORM_MEN,
  posthogEvent: "men50p_longform_landing",
  headline: "Meet gay men your age you'll actually want to see again",
  subline:
    "Take a quiz, get matched with a small group at a similar stage of life, show up to a table we already booked.",

  heroImages: HERO_MEN_50P,

  testimonials: {
    title: "What members say after their first table",
    items: TESTIMONIALS_MEN_50P,
  },

  qualifier: {
    question: "Ready to meet gay men in your city?",
    yes: "Yes",
    no: "Not right now",
    noReply: "Totally fair. We'll be here when the timing is better.",
  },

  story: {
    headline: "The places where gay friendships used to start are disappearing.",
    body: [
      "Outclose started because the third spaces went first. The bars closed, the bookstores closed, and what replaced them was an app built for something else entirely.",
      "That hits hardest if your friendships were built somewhere that no longer exists, or if the people you came up with have moved, coupled off, or gone quiet.",
      "So we built the opposite. Light on tech, heavy on hospitality. A quiz, a matching algorithm, a vetted venue, and a small group of gay men who all showed up for the same reason.",
    ],
  },

  benefits: [
    "Meet gay men at a similar life stage",
    "Build a circle in your own city",
    "Somewhere to go that isn't a bar",
    "Conversations that get past small talk",
    "Venues you'd go back to on your own",
    "A standing reason to leave the house",
  ],

  close: {
    headline: "Making gay friends after 50 shouldn't be this hard.",
    sub: "But it is.",
    body: [
      "The apps are pointed at something else entirely. The bars are loud and the room got younger. The friends you had are scattered, or partnered, or gone quiet.",
      "So people stop trying, and call it being busy.",
      "Here's the alternative. One table. A few hours. A small group of gay men who took the same quiz and booked the same night, in a place we already picked for you.",
      "You don't have to organize anything. You just have to show up.",
    ],
  },
};

/**
 * Sapphic 50+. Two known gaps, both flagged rather than faked: the photo
 * library's oldest sapphic casting is sap4050, and the sapphic quote pool tops
 * out at 46, so this page ships with the proof strip instead of testimonials
 * until real 50+ quotes are sourced.
 */
export const LONGFORM_SAPPHIC_50P: LongformConfig = {
  ...LONGFORM_SAPPHIC,
  posthogEvent: "lesbian50p_longform_landing",
  headline: "Meet lesbians your age you'll actually want to see again",
  subline:
    "Take a quiz, get matched with a small group at a similar stage of life, show up to a table we already booked.",

  heroImages: HERO_SAPPHIC_50P,
  testimonials: undefined,

  stepsTitle: "No apps, no group chat that never turns into plans. Just show up.",
  steps: [
    {
      title: "Take the matching quiz",
      body: "A few minutes on who you are, what you're into, and how you actually socialize. That's what the matching runs on.",
    },
    {
      title: "Pick an Outing",
      body: "Dinner, brunch or coffee, depending on your city. Pick the date that works and we handle the venue.",
    },
    {
      title: "Show up and have a good time",
      body: "We match you with a group of lesbians who fit, then send you the details. All you do is turn up.",
    },
    {
      title: "Stay in touch after",
      body: "Afterwards you pick who you'd like to see again. When it's mutual, we open the chat.",
    },
  ],

  qualifier: {
    question: "Ready to meet lesbians in your city?",
    yes: "Yes",
    no: "Not right now",
    noReply: "Totally fair. We'll be here when the timing is better.",
  },

  formatsTitle: "A small group that feels right from the first ten minutes",
  formatsIntro:
    "Nobody arrives looking for a performance. They want a good conversation, a relaxed room, and the chance to meet people who get it. No pressure, no expectations.",
  formats: [
    {
      name: "Dinners",
      body: "A long, easy meal with lesbians matched to you on age, interests and personality. The format we run most often, in every city.",
    },
    {
      name: "Brunch",
      body: "The same matched table, earlier and lighter. In select cities, with more added as they open.",
    },
    {
      name: "Coffee",
      body: "The lowest-commitment way in. Select neighborhoods, no reservation, you find each other in the table chat.",
    },
    {
      name: "Your table, not a room",
      body: "Every format is one small group at one table. Big events are easy to disappear in. A table is not.",
    },
  ],

  story: {
    headline: "Most cities are down to one lesbian space. Some are down to none.",
    body: [
      "Outclose started because the third spaces went first. The bars closed, the bookstores closed, and what replaced them was an app built for something else entirely.",
      "That hits hardest if the places you came up in are gone, or if the room you walk into now is two decades younger than you.",
      "So we built the opposite. Light on tech, heavy on hospitality. A quiz, a matching algorithm, a vetted venue, and a small group of lesbians who all showed up for the same reason.",
    ],
  },

  benefits: [
    "Meet lesbians at a similar life stage",
    "Build a circle in your own city",
    "Somewhere to go that isn't a bar",
    "Conversations that get past small talk",
    "Venues you'd go back to on your own",
    "A standing reason to leave the house",
  ],

  why: [
    {
      title: "Matched, not random",
      body: "Age, life stage, values, personality and social energy. Talkers get seated with listeners on purpose.",
    },
    {
      title: "A lesbian table",
      body: "Book a women's table and that's who's there. No explaining yourself, no being the only one.",
    },
    {
      title: "Venues we vet",
      body: "Welcoming places, chosen so a group can actually hear each other talk.",
    },
    {
      title: "Every week",
      body: "This isn't a one-off event you have to catch. There's another Outing next week, and the one after.",
    },
    {
      title: "The plan is already made",
      body: "Booked venue, set time, group sorted. The part where nothing gets planned is handled.",
    },
    {
      title: "It keeps going after",
      body: "Mutual picks unlock contacts, so a good night can become a group chat instead of a nice memory.",
    },
  ],

  close: {
    headline: "Making lesbian friends after 50 shouldn't be this hard.",
    sub: "But it is.",
    body: [
      "The apps are pointed at something else entirely. The one bar left is the same crowd, and the room got younger. The friends you had are scattered, or partnered, or gone quiet.",
      "So people stop trying, and call it being busy.",
      "Here's the alternative. One table. A few hours. A small group of lesbians who took the same quiz and booked the same night, in a place we already picked for you.",
      "You don't have to organize anything. You just have to show up.",
    ],
  },
};
